import { useMemo, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import type { Entry } from '../api/entries'
import { getDailyFeed, getFeedDates } from '../api/entries'
import { EntryCard } from '../components/Entry/EntryCard'
import { EntryComposer } from '../components/Entry/EntryComposer'
import { MiniCalendar } from '../components/MiniCalendar'
import { useUsername } from '../hooks/useUsername'
import { useAuthStore } from '../store/auth'
import { useAuthorTheme } from '../hooks/useAuthorTheme'
import { localDateString } from '../utils/dates'
import { usePageHeader } from '../hooks/usePageHeader'

export function Daily() {
  const username = useUsername()
  const currentUser = useAuthStore((s) => s.username)
  useAuthorTheme(username ?? undefined)
  const [searchParams] = useSearchParams()
  const initialTag = searchParams.get('initialTag')
  const initialBody = searchParams.get('body') ?? undefined
  const [entries, setEntries] = useState<Entry[]>([])
  const [date, setDate] = useState(() => localDateString())
  const [loading, setLoading] = useState(true)
  const [datesWithEntries, setDatesWithEntries] = useState<Set<string>>(new Set())
  const [showCalendar, setShowCalendar] = useState(false)

  useEffect(() => {
    if (!username) return
    getFeedDates(username)
      .then(dates => setDatesWithEntries(new Set(dates)))
      .catch(console.error)
  }, [username])

  useEffect(() => {
    if (!username) return
    setLoading(true)
    getDailyFeed(username, date)
      .then(setEntries)
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [username, date])

  function handleDateChange(newDate: string) {
    if (newDate <= localDateString()) setDate(newDate)
  }

  const titleNode = useMemo(() => (
    username ? (
      <><Link to={`/~${username}`} style={{ color: 'inherit', textDecoration: 'none', opacity: 0.6 }}>~{username}</Link>{' / journal'}</>
    ) : 'journal'
  ), [username])

  const headerActions = useMemo(() => username ? (
    <a href={`/~${username}/feed.atom`} style={{ fontSize: '0.85rem' }}>atom feed</a>
  ) : null, [username])

  usePageHeader(titleNode, headerActions)

  return (
    <div>
      {currentUser === username && (
        <EntryComposer
          initialTags={initialTag ? [initialTag] : undefined}
          initialBody={initialBody}
          onCreated={(e) => {
            setEntries((prev) => [e, ...prev])
            setDatesWithEntries(prev => new Set([...prev, date]))
          }}
        />
      )}

      <div>
        <div
          onClick={() => setShowCalendar(s => !s)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4em', cursor: 'pointer', opacity: 0.7, margin: '0.5rem 0' }}
        >
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ display: 'block' }}>
            <rect x="0.75" y="1.75" width="11.5" height="10.5" rx="1"/>
            <line x1="0.75" y1="5.25" x2="12.25" y2="5.25"/>
            <line x1="3.75" y1="0" x2="3.75" y2="3.5"/>
            <line x1="9.25" y1="0" x2="9.25" y2="3.5"/>
          </svg>
          <span>{date}</span>
        </div>
        {showCalendar && <MiniCalendar date={date} onChange={handleDateChange} datesWithEntries={datesWithEntries} />}
      </div>

      {loading ? (
        <p>loading…</p>
      ) : entries.length === 0 ? (
        <p style={{ opacity: 0.5 }}>no entries for {date}</p>
      ) : (
        entries.map((e) => (
          <EntryCard
            key={e.id}
            entry={e}
            onArchived={(id) => setEntries((prev) => prev.filter((x) => x.id !== id))}
          />
        ))
      )}
    </div>
  )
}
